import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  User,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  signInWithPopup,
  sendPasswordResetEmail,
  GoogleAuthProvider
} from 'firebase/auth';
import { auth, db } from '../lib/firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';

interface UserProfile {
  firstName?: string;
  lastName?: string;
  email: string;
}

interface UserPreferences {
  genres: string[];
  goals: {
    booksPerYear: number;
    readingTimePerDay: number;
    readingDaysPerWeek: number;
  };
  hasCompletedOnboarding: boolean;
}

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, firstName: string, lastName: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  userPreferences: UserPreferences | null;
  updateUserPreferences: (preferences: Partial<UserPreferences>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Initialize Google Provider
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

export const FirebaseAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userPreferences, setUserPreferences] = useState<UserPreferences | null>(null);

  console.log('FirebaseAuthProvider initializing...');

  const fetchUserData = async (user: User) => {
    console.log('Fetching user data for:', user.uid);
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        console.log('User data fetched:', data);
        setUserProfile({
          firstName: data.firstName,
          lastName: data.lastName,
          email: user.email || ''
        });
        setUserPreferences(data as UserPreferences);
      } else {
        console.log('No user document found');
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setError('Failed to fetch user data. Please try again later.');
    }
  };

  useEffect(() => {
    console.log('Setting up auth state listener...');
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('Auth state changed:', user ? 'User logged in' : 'No user');
      setUser(user);
      if (user) {
        await fetchUserData(user);
      } else {
        setUserProfile(null);
        setUserPreferences(null);
      }
      setLoading(false);
    });

    return () => {
      console.log('Cleaning up auth state listener');
      unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    setError(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Error signing in:', error);
      setError('Failed to sign in. Please check your credentials and try again.');
      throw error;
    }
  };

  const signUp = async (email: string, password: string, firstName: string, lastName: string) => {
    setError(null);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        firstName,
        lastName,
        email,
        hasCompletedOnboarding: false,
        genres: [],
        goals: {
          booksPerYear: 12,
          readingTimePerDay: 30,
          readingDaysPerWeek: 5
        }
      });
    } catch (error) {
      console.error('Error signing up:', error);
      setError('Failed to create account. Please try again.');
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    setError(null);
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) {
        await setDoc(doc(db, 'users', user.uid), {
          firstName: user.displayName?.split(' ')[0] || '',
          lastName: user.displayName?.split(' ').slice(1).join(' ') || '',
          email: user.email || '',
          hasCompletedOnboarding: false,
          genres: [],
          goals: {
            booksPerYear: 12,
            readingTimePerDay: 30,
            readingDaysPerWeek: 5
          }
        });
      }
      
      await fetchUserData(user);
    } catch (error) {
      console.error('Error signing in with Google:', error);
      setError('Failed to sign in with Google. Please try again.');
      throw error;
    }
  };

  const handleSignOut = async () => {
    setError(null);
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      setError('Failed to sign out. Please try again.');
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    setError(null);
    try {
      await sendPasswordResetEmail(auth, email, {
        url: `${window.location.origin}/reset-password`,
        handleCodeInApp: true
      });
    } catch (error) {
      console.error('Error resetting password:', error);
      setError('Failed to send password reset email. Please try again.');
      throw error;
    }
  };

  const updateUserPreferences = async (preferences: Partial<UserPreferences>) => {
    if (!user) {
      setError('No authenticated user found');
      throw new Error('No authenticated user found');
    }

    setError(null);
    let didTimeout = false;
    const timeout = setTimeout(() => {
      didTimeout = true;
      setError('Operation timed out. Please check your connection and try again.');
      console.error('updateUserPreferences: Operation timed out.');
    }, 15000); // Increased timeout to 15 seconds

    try {
      const userRef = doc(db, 'users', user.uid);
      
      // First, get the current user data to ensure we have the complete structure
      const userDoc = await getDoc(userRef);
      if (!userDoc.exists()) {
        // Initialize with default values if document doesn't exist
        const defaultPreferences: UserPreferences = {
          genres: [],
          goals: {
            booksPerYear: 12,
            readingTimePerDay: 30,
            readingDaysPerWeek: 5
          },
          hasCompletedOnboarding: false
        };
        await setDoc(userRef, defaultPreferences);
      }

      // Now update with the new preferences
      console.log('updateUserPreferences: About to update document', { userRef, preferences });
      await setDoc(userRef, preferences, { merge: true });
      
      clearTimeout(timeout);
      if (didTimeout) return;
      
      // Fetch the updated document to ensure we have the latest state
      const updatedDoc = await getDoc(userRef);
      const updatedData = updatedDoc.data() as UserPreferences;
      
      console.log('updateUserPreferences: Update completed successfully', updatedData);
      setUserPreferences(updatedData);
    } catch (error) {
      clearTimeout(timeout);
      console.error('Error updating preferences:', error);
      if (error instanceof Error) {
        setError(`Failed to update preferences: ${error.message}`);
      } else {
        setError('Failed to update preferences. Please try again.');
      }
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      userProfile,
      loading,
      error,
      signIn,
      signUp,
      signInWithGoogle,
      signOut: handleSignOut,
      resetPassword,
      userPreferences,
      updateUserPreferences
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within a FirebaseAuthProvider');
  }
  return context;
}; 